import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell, Gift, X, Sparkles, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { monthlyBenefits } from './constants';
import type { Redemption } from './types';
import { getCurrentMonthYear } from './utils';

interface NotificationBellProps {
  plan: 'bronce' | 'plata' | 'oro';
  redemptions: Redemption[];
  memberName: string;
  onViewRedemptions?: () => void;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  plan,
  redemptions,
  memberName,
  onViewRedemptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [bellPosition, setBellPosition] = useState({ top: 0, left: 0 });
  const bellRef = useRef<HTMLButtonElement>(null);
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearAutoCloseTimer = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  const startAutoCloseTimer = () => {
    clearAutoCloseTimer();
    autoCloseTimerRef.current = setTimeout(() => {
      setIsClosing(true);
    }, 5000);
  };

  // Update bell position when opening
  useEffect(() => {
    if (isOpen && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      setBellPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Auto-close after 5 seconds (pauses while hovering on desktop)
  useEffect(() => {
    if (isOpen && !isClosing) {
      startAutoCloseTimer();
    }

    return () => {
      clearAutoCloseTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isClosing]);

  // Handle closing animation completion
  useEffect(() => {
    if (isClosing) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isClosing]);

  const handleClose = () => {
    clearAutoCloseTimer();
    setIsClosing(true);
  };

  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
      setIsClosing(false);
    }
  };

  const unusedBenefits = useMemo(() => {
    const currentMonth = getCurrentMonthYear();
    const planBenefits = monthlyBenefits[plan];
    
    if (!planBenefits) return { items: [], total: 0 };

    const usedThisMonth: Record<string, number> = {};
    redemptions
      .filter(r => r.month_year === currentMonth)
      .forEach(r => {
        usedThisMonth[r.benefit_type] = (usedThisMonth[r.benefit_type] || 0) + r.quantity;
      });

    const items: { key: string; name: string; remaining: number; icon: string }[] = [];
    let total = 0;

    Object.entries(planBenefits).forEach(([key, benefit]) => {
      const used = usedThisMonth[key] || 0;
      const remaining = benefit.max - used;
      if (remaining > 0) {
        items.push({
          key,
          name: benefit.name,
          remaining,
          icon: benefit.icon || '🎁',
        });
        total += remaining;
      }
    });

    return { items, total };
  }, [plan, redemptions]);

  const handleCoordinate = () => {
    const message = `¡Hola! Soy ${memberName}, miembro ${plan.toUpperCase()} de VillaTech. Quiero coordinar una visita para canjear mis beneficios del mes.`;
    window.open(`https://wa.me/50663217695?text=${encodeURIComponent(message)}`, '_blank');
    handleClose();
  };

  const handleViewRedemptions = () => {
    onViewRedemptions?.();
    handleClose();
  };

  const hasNotifications = unusedBenefits.total > 0;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        ref={bellRef}
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors group"
      >
        <Bell className="w-5 h-5 text-white/70 group-hover:text-white transition-colors" />
        {hasNotifications && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-black"
          >
            {unusedBenefits.total}
          </motion.span>
        )}
      </button>

      {/* Notification Dropdown - Rendered via Portal */}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9998]"
                onClick={handleClose}
              />

              {/* Dropdown with auto-close animation */}
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={isClosing 
                  ? { opacity: 0, scale: 0.3, x: -120, y: -20 } 
                  : { opacity: 1, y: 0, scale: 1 }
                }
                exit={{ opacity: 0, scale: 0.3, x: -120, y: -20 }}
                transition={{ duration: isClosing ? 0.3 : 0.2, ease: 'easeInOut' }}
                onMouseEnter={clearAutoCloseTimer}
                onMouseLeave={() => {
                  if (!isClosing) startAutoCloseTimer();
                }}
                onPointerDown={() => {
                  if (!isClosing) startAutoCloseTimer();
                }}
                style={{
                  top: window.innerWidth < 640 ? '4rem' : bellPosition.top,
                  left: window.innerWidth < 640 ? '1rem' : Math.max(16, bellPosition.left - 320),
                  right: window.innerWidth < 640 ? '1rem' : 'auto',
                }}
                className="fixed w-auto sm:w-96 max-w-[calc(100vw-2rem)] bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl z-[9999] origin-top-left"
              >
                {hasNotifications ? (
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-3 bg-blue-500/20 rounded-xl">
                        <Gift className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-bold">
                            ¡Tenés {unusedBenefits.total} beneficios sin usar!
                          </h3>
                          <span className="w-4 h-4 rounded-full border-2 border-amber-400 flex items-center justify-center">
                            <span className="text-amber-400 text-[10px] font-bold">!</span>
                          </span>
                        </div>
                        <p className="text-white/50 text-sm mt-1">
                          No pierdas tus beneficios de este mes. Visitá la tienda para canjearlos.
                        </p>
                      </div>
                      <button
                        onClick={handleClose}
                        className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-white/40" />
                      </button>
                    </div>

                    {/* Benefits List */}
                    <div className="space-y-2 mb-4">
                      {unusedBenefits.items.map((item) => (
                        <div
                          key={item.key}
                          className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/5"
                        >
                          <span className="text-lg">{item.icon}</span>
                          <span className="text-white text-sm">
                            {item.remaining} {item.name}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={handleViewRedemptions}
                        className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
                      >
                        <Gift className="w-5 h-5" />
                        Ver Canjes
                      </button>
                      <button
                        onClick={handleCoordinate}
                        className="flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
                      >
                        <MessageCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <div className="p-3 bg-green-500/20 rounded-xl w-fit mx-auto mb-3">
                      <Sparkles className="w-6 h-6 text-green-400" />
                    </div>
                    <h3 className="text-white font-bold mb-1">¡Todo al día!</h3>
                    <p className="text-white/50 text-sm">
                      Ya canjeaste todos tus beneficios de este mes.
                    </p>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
